import React, { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile ウィジェット。
 *
 * サイトキー（`VITE_TURNSTILE_SITE_KEY`）が未設定の場合は**何も描画せず、
 * トークンも要求しない**。キーの用意前でもフォームが壊れないようにするため。
 * サーバー側も同様にシークレット未設定なら検証をスキップするので、
 * 「キーを入れた瞬間に両側で有効になる」形になっている。
 *
 * 大半の利用者には操作が発生しない（怪しい場合のみチャレンジが出る）。
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
    __turnstileScriptLoading?: Promise<void>;
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** スクリプトは1回だけ読み込む（複数フォームが同時に存在しても重複させない）。 */
const loadScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileScriptLoading) return window.__turnstileScriptLoading;

  window.__turnstileScriptLoading = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(el);
  });
  return window.__turnstileScriptLoading;
};

interface TurnstileProps {
  /** 検証トークンを受け取る。未設定環境では呼ばれない。 */
  onToken: (token: string) => void;
}

const Turnstile: React.FC<TurnstileProps> = ({ onToken }) => {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY || !ref.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          language: 'ja',
          callback: (token: string) => onToken(token),
          // トークンは一定時間で失効する。期限切れ時は再取得させる。
          'expired-callback': () => window.turnstile?.reset(widgetId.current ?? undefined),
          'error-callback': () => setFailed(true),
        });
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }
    };
    // onToken は毎レンダーで変わりうるが、ウィジェットは1度だけ作りたい
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;

  return (
    <div style={{ margin: '0 0 14px' }}>
      <div ref={ref} />
      {failed && (
        <p style={{ fontSize: '12px', color: '#B45309', marginTop: '6px' }}>
          認証の読み込みに失敗しました。通信環境をご確認のうえ、ページを再読み込みしてください。
        </p>
      )}
    </div>
  );
};

export default Turnstile;
